import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsightsIcon from '@mui/icons-material/Insights';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { Box, Button, Chip, Divider, Fade, Grow, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { AttendantGoalAiInsightView, AiLocale, useInsightPolling } from '../../../components/AttendantGoalAiInsight';
import { DEFAULT_API_ADMIN } from '../../../constants';
import { __t } from '../../../i18n/t';
import { getChainId, httpClientReactAdmin } from '../../../lib';
import { AttendantGoalType } from '../../../models';
import { InsightResponse, unwrapData } from '../../../models/attendantGoalOffer';

interface SimulationData {
  attendantDailyAverages: number[];
  suggestions: {
    easy: { value: number; attendantsAbove: number };
    moderate: { value: number; attendantsAbove: number };
    challenging: { value: number; attendantsAbove: number };
  } | null;
  totalAttendants: number;
  period: { days: number; activeDays: number };
}

interface GoalSimulatorProps {
  targetType: string;
  minimumAmount: number;
  index: number;
  stationIds?: string[];
  fuelIds?: string[];
  daysOfWeek?: string[];
  selectsAllPlaces?: boolean;
  selectsAllFuels?: boolean;
  numeratorFuelIds?: string[];
  denominatorFuelIds?: string[];
}

const DAY_LABELS: Record<string, string> = {
  monday: 'Seg', tuesday: 'Ter', wednesday: 'Qua', thursday: 'Qui',
  friday: 'Sex', saturday: 'Sáb', sunday: 'Dom', holiday: 'Feriado',
};

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const SUGGESTION_CONFIG = [
  { key: 'easy', label: 'Acessível', color: '#e8f5e9', borderColor: '#81c784', textColor: '#2e7d32', Icon: SentimentSatisfiedAltIcon, description: '75% atinge' },
  { key: 'moderate', label: 'Moderado', color: '#fff3e0', borderColor: '#ffb74d', textColor: '#e65100', Icon: TrendingUpIcon, description: '50% atinge' },
  { key: 'challenging', label: 'Desafiador', color: '#fce4ec', borderColor: '#e57373', textColor: '#c62828', Icon: LocalFireDepartmentIcon, description: '25% atinge' },
] as const;

const shimmerKeyframes = {
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
};

// ── Sugerir com IA (feature 161) ──────────────────────────────────────────
type AiLevel = 'conservative' | 'balanced' | 'challenging';
interface AiLevelOption { level: AiLevel; value: number; daysHitRatePct: number; attendantsReaching: number; }
interface AiLevelSet {
  options: AiLevelOption[];
  singleLevel: boolean;
  recommended: AiLevel;
  reason: 'default' | 'high_dispersion' | 'small_team';
  eligibleAttendants: number;
}
interface SuggestResult { levels: AiLevelSet | null; insight: InsightResponse; moduleInactive?: boolean; }

// Só estes tipos o motor sabe medir (contrato seção 2: MeasuredGoalType).
const AI_TARGET_TYPES: string[] = [AttendantGoalType.QUANTITY, AttendantGoalType.PERCENTAGE_LOYALTY, AttendantGoalType.VOLUME];
const AI_GRADIENT = 'linear-gradient(135deg, #6366f1, #a855f7)';
const AI_LEVEL_COLORS: Record<AiLevel, { bg: string; border: string; text: string }> = {
  conservative: { bg: '#e8f5e9', border: '#81c784', text: '#2e7d32' },
  balanced: { bg: '#fff3e0', border: '#ffb74d', text: '#e65100' },
  challenging: { bg: '#fce4ec', border: '#e57373', text: '#c62828' },
};

// Texto traduzido sempre no render (o idioma troca sem reload).
const aiLevelLabel = (level: AiLevel): string => {
  switch (level) {
    case 'conservative': return __t('Conservadora');
    case 'balanced': return __t('Equilibrada');
    default: return __t('Desafiadora');
  }
};

const aiReasonText = (reason: AiLevelSet['reason']): string | null => {
  if (reason === 'high_dispersion') return __t('Equipe com ritmos bem diferentes: recomendamos começar pelo nível Conservadora.');
  if (reason === 'small_team') return __t('Equipe pequena: recomendamos começar pelo nível Conservadora.');
  return null;
};

const formatGoalValue = (type: string, value: number): string => {
  if (type === AttendantGoalType.PERCENTAGE_LOYALTY) return `${value}%`;
  if (type === AttendantGoalType.VOLUME) return `${value} L`;
  return String(value);
};

const ModuleInactiveNotice = () => (
  <Box sx={{ mt: 1.5, py: 1.5, px: 1.5, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>Módulo desativado</Typography>
    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>O módulo Meta de Frentista está desativado para a sua rede.</Typography>
  </Box>
);

interface AiSuggestionCardProps {
  targetType: string;
  loading: boolean;
  result: SuggestResult | null;
  chosen: AiLevel | null;
  canApply: boolean;
  onChoose: (level: AiLevel) => void;
  onApply: (option: AiLevelOption) => void;
  onClose: () => void;
}

const AiSuggestionCard: React.FC<AiSuggestionCardProps> = ({ targetType, loading, result, chosen, canApply, onChoose, onApply, onClose }) => {
  const levels = result?.levels ?? null;
  // recomendado pode ter sumido se níveis iguais se fundiram: cai na primeira opção
  const option = levels ? (levels.options.find((o) => o.level === (chosen ?? levels.recommended)) ?? levels.options[0]) : null;
  const reason = levels ? aiReasonText(levels.reason) : null;

  let body: React.ReactNode;
  if (loading) {
    body = (
      <Box sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(102,126,234,0.6)', mb: 1, fontStyle: 'italic' }}>
          {__t('A IA está montando a sugestão…')}
        </Typography>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{
            height: 32, borderRadius: '8px', mb: 0.8,
            background: 'linear-gradient(90deg, rgba(102,126,234,0.06) 25%, rgba(118,75,162,0.12) 50%, rgba(102,126,234,0.06) 75%)',
            backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </Box>
    );
  } else if (!result) {
    body = <Typography sx={{ mt: 1, fontSize: '0.78rem', color: 'text.secondary' }}>{__t('Não foi possível sugerir agora. Tente novamente em instantes.')}</Typography>;
  } else if (result.moduleInactive) {
    body = <ModuleInactiveNotice />;
  } else if (!levels || !option) {
    body = <Typography sx={{ mt: 1, fontSize: '0.78rem', color: 'text.secondary' }}>{__t('Ainda não há histórico suficiente para sugerir uma meta nestes postos e dias.')}</Typography>;
  } else {
    body = (
      <Box sx={{ mt: 1.2 }}>
        <Box display="flex" gap={1} flexWrap="wrap">
          {levels.options.map((o) => {
            const colors = AI_LEVEL_COLORS[o.level];
            const selected = o.level === option.level;
            return (
              <Box
                key={o.level}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => onChoose(o.level)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onChoose(o.level); }}
                sx={{
                  minWidth: 170, px: 1.5, py: 1, borderRadius: '10px', cursor: 'pointer',
                  bgcolor: colors.bg, border: `${selected ? 2 : 1}px solid ${colors.border}`,
                  boxShadow: selected ? `0 3px 12px ${colors.border}66` : 'none',
                  transition: 'all 0.2s',
                }}
              >
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Typography sx={{ fontSize: '0.7rem', color: colors.text, fontWeight: 700 }}>{aiLevelLabel(o.level)}</Typography>
                  {o.level === levels.recommended && <Chip label={__t('Recomendada')} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: colors.border, color: '#fff' }} />}
                </Box>
                <Typography sx={{ fontSize: '1.05rem', color: colors.text, fontWeight: 800, lineHeight: 1.3 }}>{formatGoalValue(targetType, o.value)}</Typography>
                <Typography sx={{ fontSize: '0.64rem', color: colors.text }}>
                  {__t('%{n} de %{total} frentistas já bateriam', { n: o.attendantsReaching, total: levels.eligibleAttendants })}
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', color: `${colors.text}b3` }}>
                  {__t('Dias batidos no histórico: %{pct}%', { pct: o.daysHitRatePct })}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {levels.singleLevel && (
          <Typography sx={{ mt: 0.8, fontSize: '0.7rem', color: 'text.secondary' }}>{__t('Com este histórico só há um valor possível.')}</Typography>
        )}
        {reason && <Typography sx={{ mt: 0.8, fontSize: '0.7rem', color: 'text.secondary' }}>{reason}</Typography>}
        <AttendantGoalAiInsightView response={result.insight} sx={{ mt: 1.25 }} />
        <Button
          variant="contained"
          size="small"
          disabled={!canApply}
          onClick={() => onApply(option)}
          sx={{ mt: 1.25, background: AI_GRADIENT, textTransform: 'none', fontSize: '0.76rem', boxShadow: 'none' }}
        >
          {__t('Aplicar esta meta')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5, maxWidth: 920, p: 1.5, borderRadius: '12px', border: '1px solid rgba(139,92,246,0.22)', background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(168,85,247,0.06))' }}>
      <Box display="flex" alignItems="center" gap={1}>
        <AutoAwesomeIcon sx={{ fontSize: '1rem', color: '#7c3aed' }} />
        <Typography sx={{ fontSize: '0.83rem', fontWeight: 700, color: '#7c3aed' }}>{__t('Sugerir com IA')}</Typography>
        <Button size="small" onClick={onClose} sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary' }}>
          {__t('Fechar')}
        </Button>
      </Box>
      {body}
    </Box>
  );
};

const GoalSimulator: React.FC<GoalSimulatorProps> = ({
  targetType, minimumAmount, index,
  stationIds, fuelIds, daysOfWeek,
  selectsAllPlaces, selectsAllFuels,
  numeratorFuelIds, denominatorFuelIds,
}) => {
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SimulationData | null>(null);
  const [fetchedForType, setFetchedForType] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [moduleInactive, setModuleInactive] = useState(false);
  // aiNonce só cresce (nunca volta a 0): cada pedido tem chave própria e nunca reaproveita resultado de outro.
  const [aiNonce, setAiNonce] = useState(0);
  // Tipo para o qual o card foi aberto. aiOpen é derivado no render: ao trocar o tipo, o hook já nasce desabilitado
  // (sem GET /suggest para um card que ninguém vê).
  const [aiOpenFor, setAiOpenFor] = useState<string | null>(null);
  const [aiLevel, setAiLevel] = useState<AiLevel | null>(null);
  const canSuggest = AI_TARGET_TYPES.includes(targetType);
  const aiOpen = aiOpenFor === targetType && canSuggest;
  if (aiOpenFor !== null && aiOpenFor !== targetType) {
    // Trocou o tipo da meta: o card aberto era de outro tipo. Reset no render (antes de qualquer efeito).
    setAiOpenFor(null);
    setAiLevel(null);
  }

  const stationCount = selectsAllPlaces ? null : (stationIds?.length || 0);
  const activeDays = useMemo(() => (daysOfWeek || []).filter(d => d !== 'holiday').sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)), [daysOfWeek]);

  const simulationCount = useMemo(() => {
    if (!data) return null;
    const target = Number(minimumAmount) || 0;
    if (target <= 0) return null;
    const above = data.attendantDailyAverages.filter(avg => avg >= target).length;
    return { above, total: data.totalAttendants };
  }, [data, minimumAmount]);

  // Filtro comum da simulação e da sugestão: uma regra só para "todos os postos/combustíveis".
  const buildBaseFilter = useCallback(() => {
    const filter: any = { chainId: getChainId(), targetType };
    if (stationIds?.length && !selectsAllPlaces) filter.stationIds = stationIds;
    if (fuelIds?.length && !selectsAllFuels) filter.fuelIds = fuelIds;
    if (activeDays.length) filter.daysOfWeek = activeDays;
    return filter;
  }, [targetType, stationIds, fuelIds, activeDays, selectsAllPlaces, selectsAllFuels]);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    try {
      const filter = { ...buildBaseFilter(), days };
      if (numeratorFuelIds?.length) filter.numeratorFuelIds = numeratorFuelIds;
      if (denominatorFuelIds?.length) filter.denominatorFuelIds = denominatorFuelIds;

      const response = await dataProvider.getList('attendant-goal-boards/simulate', {
        filter,
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      });

      // Módulo vencido: o backend responde 200 com lista vazia e `moduleInactive` no topo do JSON (nunca 403, que derruba a sessão).
      // O dataProvider devolve o JSON cru, então a chave do topo chega (emenda 6).
      const inactive = (response as any).moduleInactive === true;
      setModuleInactive(inactive);
      if (inactive) {
        setData(null);
        return;
      }

      const result = response.data?.[0] as any;
      if (result) {
        setData(result);
        setFetchedForType(targetType);
      }
    } catch (e) {
      console.error('Simulation error:', e);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, buildBaseFilter, numeratorFuelIds, denominatorFuelIds, days]);

  const handleApplySuggestion = (value: number) => {
    setValue(`targets.${index}.minimumAmount`, value);
  };

  // Busca sugestão + Leitura da IA no mesmo GET; repete enquanto a IA diz "generating".
  const loadSuggestion = async (locale: AiLocale): Promise<SuggestResult | null> => {
    const filter = { ...buildBaseFilter(), locale };
    const response = await dataProvider.getList('attendant-goal-boards/suggest', {
      filter,
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    });
    return unwrapData<SuggestResult>(response);
  };
  const ai = useInsightPolling<SuggestResult>(loadSuggestion, (r) => r.insight, `${aiNonce}|${targetType}`, aiOpen);

  const handleSuggest = () => {
    setAiLevel(null);
    setAiNonce((n) => n + 1);
    setAiOpenFor(targetType);
  };

  const handleCloseAi = () => {
    setAiOpenFor(null);
    setAiLevel(null);
  };

  const handleApplyAi = (option: AiLevelOption) => {
    if (index < 0) return; // alvo não encontrado no array do form
    setValue(`targets.${index}.minimumAmount`, Number(option.value), { shouldDirty: true });
    // Só avisa o Discord da equipe; falha aqui não atrapalha quem está montando o quadro.
    httpClientReactAdmin(`${DEFAULT_API_ADMIN}/attendant-goal-boards/suggest/applied`, {
      method: 'POST',
      body: JSON.stringify({ targetType, level: option.level }),
    }).catch(() => undefined);
  };

  const showResults = data && fetchedForType === targetType && !loading;

  return (
    <Box
      sx={{
        border: '1px solid rgba(102, 126, 234, 0.15)',
        borderRadius: '12px',
        background: 'rgba(102, 126, 234, 0.03)',
        p: 2,
        mt: 0,
        mb: 3,
        ...shimmerKeyframes,
      }}
    >
      {/* Context chips + button */}
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <InsightsIcon sx={{ color: '#667eea', fontSize: '1.1rem' }} />
        <Typography sx={{ fontSize: '0.83rem', color: '#667eea', fontWeight: 600, mr: 0.5 }}>
          Sugestão de Meta
        </Typography>

        <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
          {activeDays.map(d => (
            <Chip
              key={d}
              label={DAY_LABELS[d] || d}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea', fontWeight: 500 }}
            />
          ))}
          <Chip
            label={stationCount === null ? 'Todos os postos' : `${stationCount} posto${stationCount !== 1 ? 's' : ''}`}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea', fontWeight: 500 }}
          />
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: 'rgba(102,126,234,0.2)' }} />

        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>últimos</Typography>
          <input
            type="number"
            min={7}
            max={180}
            value={days}
            onChange={e => setDays(Math.min(180, Math.max(7, Number(e.target.value) || 30)))}
            style={{
              width: 36,
              textAlign: 'center',
              border: 'none',
              borderBottom: '1px solid rgba(102,126,234,0.4)',
              background: 'transparent',
              color: '#667eea',
              fontWeight: 700,
              fontSize: '0.76rem',
              outline: 'none',
              padding: '0 2px',
            }}
          />
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>dias</Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} sx={{ ml: 'auto' }}>
          {canSuggest && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={handleSuggest}
              disabled={aiOpen && ai.loading}
              sx={{ background: AI_GRADIENT, textTransform: 'none', fontSize: '0.76rem', py: 0.3, px: 1.5, boxShadow: 'none' }}
            >
              {aiOpen ? __t('Atualizar sugestão') : __t('Sugerir com IA')}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<InsightsIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={handleFetch}
            disabled={loading}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              fontSize: '0.76rem',
              textTransform: 'none',
              py: 0.3,
              px: 1.5,
              '&:hover': { background: 'rgba(102,126,234,0.08)', borderColor: '#5a6fd6' },
            }}
          >
            {showResults ? 'Atualizar' : 'Analisar histórico'}
          </Button>
        </Box>
      </Box>

      {moduleInactive && <ModuleInactiveNotice />}

      {/* Sugerir com IA */}
      {aiOpen && canSuggest && (
        <AiSuggestionCard
          targetType={targetType}
          loading={ai.loading}
          result={ai.data}
          chosen={aiLevel}
          canApply={index >= 0}
          onChoose={setAiLevel}
          onApply={handleApplyAi}
          onClose={handleCloseAi}
        />
      )}

      {/* Loading shimmer */}
      {loading && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(102,126,234,0.6)', mb: 1, fontStyle: 'italic' }}>
            Analisando dados de {stationCount === null ? 'todos os postos' : `${stationCount} posto${stationCount !== 1 ? 's' : ''}`} nos últimos {days} dias...
          </Typography>
          {[1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{
                height: 32,
                borderRadius: '8px',
                mb: 0.8,
                background: 'linear-gradient(90deg, rgba(102,126,234,0.06) 25%, rgba(118,75,162,0.12) 50%, rgba(102,126,234,0.06) 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </Box>
      )}

      {/* Results */}
      {showResults && (
        <Fade in timeout={400}>
          <Box sx={{ mt: 1.5 }}>
            {/* Header */}
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.2 }}>
              Análise baseada em <b>{data.totalAttendants} frentista{data.totalAttendants !== 1 ? 's' : ''}</b> · {stationCount === null ? 'todos os postos' : `${stationCount} posto${stationCount !== 1 ? 's' : ''}`} · últimos <b>{days} dias</b>
              {activeDays.length > 0 && activeDays.length < 7 && (
                <> ({activeDays.map(d => DAY_LABELS[d]).join(', ')})</>
              )}
            </Typography>

            {/* Empty state */}
            {data.totalAttendants === 0 && (
              <Box sx={{ py: 1.5, px: 1.5, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  Nenhum dado encontrado para os postos e período selecionados. Verifique se os frentistas possuem tag cadastrada e se há abastecimentos registrados.
                </Typography>
              </Box>
            )}

            {/* Suggestion chips */}
            {data.suggestions && !aiOpen && (
              <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                {SUGGESTION_CONFIG.map(({ key, label, color, borderColor, textColor, Icon, description }, idx) => {
                  const suggestion = data.suggestions![key as keyof typeof data.suggestions];
                  if (!suggestion) return null;
                  return (
                    <Grow in timeout={400 + idx * 150} key={key}>
                      <Box
                        onClick={() => handleApplySuggestion(suggestion.value)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          px: 1.5,
                          py: 0.8,
                          borderRadius: '10px',
                          bgcolor: color,
                          border: `1px solid ${borderColor}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 3px 12px ${borderColor}44` },
                        }}
                      >
                        <Icon sx={{ fontSize: '1rem', color: textColor }} />
                        <Box>
                          <Typography sx={{ fontSize: '0.68rem', color: textColor, fontWeight: 600, lineHeight: 1.2 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.88rem', color: textColor, fontWeight: 800, lineHeight: 1.2 }}>
                            {suggestion.value}
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: `${textColor}99`, lineHeight: 1.2 }}>
                            {description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grow>
                  );
                })}
              </Box>
            )}

            {/* Live simulation count */}
            {simulationCount && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: '8px',
                  bgcolor: simulationCount.above === 0 ? 'rgba(198,40,40,0.06)' : simulationCount.above === simulationCount.total ? 'rgba(46,125,50,0.06)' : 'rgba(102,126,234,0.06)',
                  border: `1px solid ${simulationCount.above === 0 ? 'rgba(198,40,40,0.15)' : simulationCount.above === simulationCount.total ? 'rgba(46,125,50,0.15)' : 'rgba(102,126,234,0.12)'}`,
                }}
              >
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  Com meta de <b>{minimumAmount}</b>,{' '}
                  <Typography component="span" sx={{ fontWeight: 800, fontSize: '0.88rem', color: simulationCount.above === 0 ? '#c62828' : simulationCount.above === simulationCount.total ? '#2e7d32' : '#667eea' }}>
                    {simulationCount.above} de {simulationCount.total}
                  </Typography>
                  {' '}frentista{simulationCount.total !== 1 ? 's' : ''} teriam atingido a meta
                </Typography>
              </Box>
            )}

            {/* Achievement bar */}
            {simulationCount && simulationCount.total > 0 && (
              <Box sx={{ mt: 1.2 }}>
                <Box sx={{ position: 'relative', height: 10, borderRadius: 99, overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.06)' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${(simulationCount.above / simulationCount.total) * 100}%`,
                      borderRadius: 99,
                      background: simulationCount.above === simulationCount.total
                        ? 'linear-gradient(90deg, #66bb6a, #43a047)'
                        : simulationCount.above === 0
                          ? 'linear-gradient(90deg, #ef5350, #e53935)'
                          : 'linear-gradient(90deg, #667eea, #764ba2)',
                      transition: 'width 0.4s ease',
                      boxShadow: simulationCount.above > 0 ? '0 0 8px rgba(102,126,234,0.3)' : 'none',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                    {Math.round((simulationCount.above / simulationCount.total) * 100)}% bateriam
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                    {simulationCount.above}/{simulationCount.total}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default GoalSimulator;
